import {
	useAuthMe,
	useGoogleMobileLogin,
	useLogout,
	userQueryKeys,
} from "@bodhistreams/api/src";
import useUIStore from "@bodhistreams/stores/src/uiStore";
import { useGoogleOneTapLogin } from "@react-oauth/google";
import { useQueryClient } from "@tanstack/react-query";
// `theme.useToken()` is a hook, not a component — kept as the sanctioned source
// of design-system colors (project rule: no hardcoded colors). All antd UI
// components have been removed in favour of plain elements + UserMenu.css.
import { theme } from "antd";
import { useEffect, useRef, useState } from "react";
import { ADMIN_URL, APP_URL, LANDING_URL } from "../../../urls";
import "./UserMenu.css";

const { useToken } = theme;

const iconProps = {
	width: "1em",
	height: "1em",
	viewBox: "0 0 24 24",
	fill: "none",
	stroke: "currentColor",
	strokeWidth: 1.8,
	strokeLinecap: "round",
	strokeLinejoin: "round",
};

const SoundIcon = (props) => (
	<svg {...iconProps} {...props} aria-hidden="true">
		<path d="M11 5 6 9H2v6h4l5 4z" />
		<path d="M15.5 8.5a5 5 0 0 1 0 7" />
		<path d="M18.5 6a8 8 0 0 1 0 12" />
	</svg>
);

const MailIcon = (props) => (
	<svg {...iconProps} {...props} aria-hidden="true">
		<rect x="3" y="5" width="18" height="14" rx="2" />
		<path d="m3 7 9 6 9-6" />
	</svg>
);

const DashboardIcon = (props) => (
	<svg {...iconProps} {...props} aria-hidden="true">
		<rect x="3" y="3" width="7" height="7" rx="1" />
		<rect x="14" y="3" width="7" height="7" rx="1" />
		<rect x="14" y="14" width="7" height="7" rx="1" />
		<rect x="3" y="14" width="7" height="7" rx="1" />
	</svg>
);

const ProfileIcon = (props) => (
	<svg {...iconProps} {...props} aria-hidden="true">
		<circle cx="12" cy="8" r="4" />
		<path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
	</svg>
);

const LogoutIcon = (props) => (
	<svg {...iconProps} {...props} aria-hidden="true">
		<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
		<path d="m16 17 5-5-5-5" />
		<path d="M21 12H9" />
	</svg>
);

const ShieldIcon = (props) => (
	<svg {...iconProps} {...props} aria-hidden="true">
		<path d="M12 3 4 6v5c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z" />
		<path d="m9 12 2 2 4-4" />
	</svg>
);

const UserMenu = ({ showStudio = false }) => {
	const { token } = useToken();
	const showLoginModal = useUIStore((state) => state.showLoginModal);
	const showRegisterModal = useUIStore((state) => state.showRegisterModal);
	const queryClient = useQueryClient();
	const { data: authUser, isLoading: isAuthMeLoading } = useAuthMe();
	const isLoggedIn = !!authUser;
	const isAuthLoading = useUIStore((state) => state.isAuthLoading);
	const { mutateAsync: googleOneTapMutation, isPending: isGoogleLoading } =
		useGoogleMobileLogin();
	const isLoading = isAuthLoading || isAuthMeLoading || isGoogleLoading;

	const [open, setOpen] = useState(false);
	const rootRef = useRef(null);

	useEffect(() => {
		if (!open) return;
		const handlePointerDown = (event) => {
			if (rootRef.current && !rootRef.current.contains(event.target)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handlePointerDown);
		return () => document.removeEventListener("mousedown", handlePointerDown);
	}, [open]);

	useGoogleOneTapLogin({
		disabled: isLoggedIn || isAuthMeLoading || isAuthLoading,
		client_id:
			"788011854098-u73tak52ass5ked9lrr0vj9pq5p20od3.apps.googleusercontent.com",
		cancel_on_tap_outside: false,
		auto_select: false,
		onSuccess: async (response) => {
			try {
				await googleOneTapMutation(response.credential);
			} catch (_err) {
				// silent — the mutation hook already handles errors
			}
		},
		onError: () => {
			// One-Tap closed / not available — not actionable, stay silent
		},
	});

	const { mutate: logoutMutation } = useLogout();

	const handleLogout = () => {
		logoutMutation(undefined, {
			onSuccess: () => {
				queryClient.removeQueries({ queryKey: userQueryKeys.profile() });
				window.location.href = LANDING_URL;
			},
			onError: () => {
				queryClient.removeQueries({ queryKey: userQueryKeys.profile() });
				window.location.href = LANDING_URL;
			},
		});
	};

	const getInitials = (name) => {
		if (!name) return "US";

		// Handle email addresses
		if (name.includes("@")) {
			// Get the part before the @ symbol
			const localPart = name.split("@")[0];

			// If the local part contains a period (like francesco.decampo)
			if (localPart.includes(".")) {
				// Split at the period and take first letter of each part
				const parts = localPart.split(".");
				return (parts[0][0] + parts[1][0]).toUpperCase();
			}
			// If the local part contains a hyphen
			else if (localPart.includes("-")) {
				// Split at the hyphen and take first letter of each part
				const parts = localPart.split("-");
				return (parts[0][0] + parts[1][0]).toUpperCase();
			}
			// For emails without periods or hyphens, take first two letters
			else {
				return localPart.substring(0, 2).toUpperCase();
			}
		}

		// For non-email names, fallback to original logic
		return name
			.split(/\s+/)
			.map((word) => word[0])
			.join("")
			.toUpperCase()
			.slice(0, 2);
	};

	const userEmailData = authUser?.email || "";
	const isAdminData = authUser?.is_admin || false;

	// Format the username for display
	const displayName = userEmailData
		? userEmailData.split("@")[0].replace(/[.-]/g, " ")
		: "User";
	const formattedName = displayName
		.split(" ")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");

	// Feed the design-system tokens into CSS variables consumed by UserMenu.css.
	const rootStyle = {
		"--bsum-bg": token.colorBgElevated,
		"--bsum-elevated": token.colorBgElevated,
		"--bsum-header-bg": token.colorBgUserHeader,
		"--bsum-primary": token.colorPrimary,
		"--bsum-primary-hover": token.colorPrimaryHover,
		"--bsum-primary-soft": token.colorPrimaryBg,
		"--bsum-text": token.colorText,
		"--bsum-text-secondary": token.colorTextSecondary,
		"--bsum-hover": token.colorBgTextHover,
		"--bsum-border": token.colorBorder,
		"--bsum-danger": token.colorError,
		"--bsum-danger-soft": token.colorErrorBg,
		"--bsum-radius": `${token.borderRadius}px`,
	};

	const initials = getInitials(userEmailData);

	return (
		<div className="um-root" style={rootStyle} ref={rootRef}>
			{isLoading ? (
				<span className="um-spinner" />
			) : isLoggedIn ? (
				<>
					<div className="um-trigger">
						{showStudio && (
							<a className="um-studio" href={APP_URL}>
								<SoundIcon />
								Go to App
							</a>
						)}
						<button
							type="button"
							className={`um-avatar${showStudio ? " um-avatar--attached" : ""}${
								open ? " um-avatar--open" : ""
							}`}
							onClick={() => setOpen((value) => !value)}
							aria-haspopup="menu"
							aria-expanded={open}
							aria-label="Open user menu"
						>
							{initials}
						</button>
					</div>

					{open && (
						<div className="um-dropdown" role="menu">
							<div className="um-header">
								<div className="um-header-main">
									<div className="um-header-avatar">{initials}</div>
									<div className="um-header-info">
										<div className="um-welcome">Welcome, {formattedName}!</div>
										<div className="um-email" title={userEmailData}>
											<MailIcon />
											<span>{userEmailData}</span>
										</div>
									</div>
								</div>

								{isAdminData && (
									<span className="um-role-badge">
										<ShieldIcon />
										Administrator
									</span>
								)}
							</div>

							<div className="um-menu">
								{isAdminData && (
									<button
										type="button"
										className="um-menu-item"
										role="menuitem"
										onClick={() => {
											window.location.href = ADMIN_URL;
										}}
									>
										<span className="um-menu-icon">
											<DashboardIcon />
										</span>
										Admin Dashboard
									</button>
								)}
								<button
									type="button"
									className="um-menu-item"
									role="menuitem"
									onClick={() => {
										window.location.href = `${LANDING_URL}/profile`;
									}}
								>
									<span className="um-menu-icon">
										<ProfileIcon />
									</span>
									Profile
								</button>
								<button
									type="button"
									className="um-menu-item"
									role="menuitem"
									onClick={() => {
										window.location.href = `${APP_URL}/support`;
									}}
								>
									<span className="um-menu-icon">
										<MailIcon />
									</span>
									Support
								</button>

								<div className="um-divider" />

								<button
									type="button"
									className="um-menu-item um-menu-item--danger"
									role="menuitem"
									onClick={handleLogout}
								>
									<span className="um-menu-icon">
										<LogoutIcon />
									</span>
									Logout
								</button>
							</div>
						</div>
					)}
				</>
			) : (
				<div className="um-auth">
					<button
						type="button"
						className="um-signup"
						onClick={showRegisterModal}
					>
						Sign Up
					</button>
					<button type="button" className="um-login" onClick={showLoginModal}>
						Log In
					</button>
				</div>
			)}
		</div>
	);
};

export default UserMenu;
